import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SectionFrame } from '@/components/layout/SectionFrame';
import { useLanguage } from '@/contexts/LanguageContext';
import { Sparkles, Target, Shield, Handshake, Building, Zap } from 'lucide-react';

type Props = {
  sectionIndex?: number;
  backgroundImage?: string | null;
};

const DEFAULT_BG = '/op-uploads/6c14e3c1-49c9-4615-ba5b-e7d6012d332e.png';

export const VisionMission: React.FC<Props> = ({ sectionIndex = 0, backgroundImage }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const reduceMotion = useReducedMotion();

  const content = React.useMemo(() => {
    if (language === 'ar') {
      return {
        visionTitle: 'رؤيتنا',
        visionBody:
          'أن تكون مجموعة أباسك المنصّة الاستثمارية الأكثر موثوقية في المملكة والمنطقة؛ نحوّل التنويع المنضبط إلى قيمةٍ مستدامة تُرى وتُقاس.',
        missionTitle: 'رسالتنا',
        missionBody:
          'نُميّز الفرص ونُحقّق الفرضيات، ثم ننفّذ بانضباط يوازن الجودة والموعد والتكلفة؛ ونبني شراكاتٍ طويلة الأمد تصنع أثرًا ملموسًا في المياه والعقار والاتصالات والتعدين.',
        cta: 'تعرّف على رؤيتنا',
        industries: ['المياه', 'العقار', 'الاتصالات', 'التعدين'],
      };
    }
    return {
      visionTitle: 'Vision',
      visionBody:
        'To position ABASC HOLDING as the region’s most trusted investment platform—turning disciplined diversification into sustainable, measurable value.',
      missionTitle: 'Mission',
      missionBody:
        'We qualify opportunities and validate assumptions, then execute with rigor to balance quality, timeline, and cost—building long-term partnerships that create tangible impact across water, real estate, telecom, and mining.',
      cta: 'Explore Our Vision',
      industries: ['Water', 'Real Estate', 'Telecom', 'Mining'],
    };
  }, [language]);

  const containerClasses = `mx-auto w-full max-w-[1200px] px-4 sm:px-6 md:px-8`;
  const cardBase =
    'relative rounded-2xl p-6 md:p-8 shadow-[0_10px_30px_rgba(0,0,0,0.25)] border border-white/10 bg-[rgba(15,23,42,0.55)] backdrop-blur-sm';
  const titleClass = `${isRTL ? 'font-arabic-heading text-right' : 'font-english-heading text-left'} text-white text-2xl md:text-3xl font-bold flex items-center gap-3`;
  const bodyClass = `${isRTL ? 'font-arabic-body text-right' : 'font-english-body text-left'} text-white/85 text-base md:text-lg leading-relaxed mt-3`;
  const gold = '#C6A15B';

  // Small breathing motion variants
  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: reduceMotion ? 0 : 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    // use a cubic-bezier easing compatible with Framer Motion types
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as [number, number, number, number], delay },
  });

  // Stagger container for group entrances
  const staggerContainer = {
    hidden: { opacity: 1 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: reduceMotion ? 0 : 0.08,
        delayChildren: reduceMotion ? 0 : 0.05,
      },
    },
  } as const;

  // Child variant for list items/chips
  const itemVariant = {
    hidden: { opacity: 0, y: reduceMotion ? 0 : 10 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
    },
  } as const;

  return (
    <SectionFrame
      className="pg-section fullpage-section relative overflow-hidden"
      data-theme="dark"
      data-section-index={sectionIndex}
      center
      style={{
        backgroundImage: `url('${backgroundImage || DEFAULT_BG}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      overlayClassName="bg-gradient-to-b from-black/60 via-black/55 to-black/70"
    >
      <style>{`.vm-accent{color:${gold}} .vm-chip{border-color:rgba(255,255,255,.2);background:rgba(255,255,255,.06)}`}</style>
      <div className="relative z-20 w-full">
        <div className={containerClasses}>
          {/* Cards wrapper */}
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-stretch`}>
            {/* Vision Card */}
            <motion.div
              {...fadeUp(0.05)}
              className={cardBase}
              whileHover={reduceMotion ? undefined : { y: -4, scale: 1.01 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] }}
            >
              <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{ boxShadow: 'inset 0 0 0 1px rgba(198,161,91,0.15)' }} />
              <div className="flex items-start gap-3">
                <div className="shrink-0 rounded-xl p-3" style={{ background: 'rgba(198,161,91,0.12)', border: '1px solid rgba(198,161,91,0.25)' }}>
                  <Sparkles className="w-6 h-6" style={{ color: gold }} />
                </div>
                <h3 className={titleClass}>
                  <span className="vm-accent">{content.visionTitle}</span>
                </h3>
              </div>
              <p className={bodyClass}>{content.visionBody}</p>
              <motion.div
                className={`mt-5 flex flex-wrap gap-3 ${isRTL ? 'justify-end' : 'justify-start'}`}
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
              >
                <motion.span
                  variants={itemVariant}
                  className="vm-chip inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-white/90 text-sm"
                  whileHover={reduceMotion ? undefined : { scale: 1.03 }}
                >
                  <motion.span animate={reduceMotion ? undefined : { rotate: [0, -8, 0] }} transition={{ repeat: Infinity, repeatDelay: 2.6, duration: 1.2 }}>
                    <Shield className="w-4 h-4" style={{ color: gold }} />
                  </motion.span>
                  {language === 'ar' ? 'ثقة' : 'Trust'}
                </motion.span>
                <motion.span
                  variants={itemVariant}
                  className="vm-chip inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-white/90 text-sm"
                  whileHover={reduceMotion ? undefined : { scale: 1.03 }}
                >
                  <motion.span animate={reduceMotion ? undefined : { scale: [1, 1.12, 1] }} transition={{ repeat: Infinity, repeatDelay: 2.2, duration: 1.1 }}>
                    <Zap className="w-4 h-4" style={{ color: gold }} />
                  </motion.span>
                  {language === 'ar' ? 'تنويع منضبط' : 'Disciplined Diversification'}
                </motion.span>
              </motion.div>
            </motion.div>

            {/* Mission Card */}
            <motion.div
              {...fadeUp(0.15)}
              className={cardBase}
              whileHover={reduceMotion ? undefined : { y: -4, scale: 1.01 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] }}
            >
              <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{ boxShadow: 'inset 0 0 0 1px rgba(127,179,255,0.15)' }} />
              <div className="flex items-start gap-3">
                <div className="shrink-0 rounded-xl p-3" style={{ background: 'rgba(127,179,255,0.12)', border: '1px solid rgba(127,179,255,0.25)' }}>
                  <Target className="w-6 h-6 text-sky-300" />
                </div>
                <h3 className={titleClass}>
                  <span className="text-sky-300">{content.missionTitle}</span>
                </h3>
              </div>
              <p className={bodyClass}>{content.missionBody}</p>
              {/* Industries */}
              <motion.div
                className={`mt-5 grid grid-cols-2 gap-3 ${isRTL ? 'text-right' : 'text-left'}`}
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
              >
                {content.industries.map((label, i) => (
                  <motion.span
                    key={i}
                    variants={itemVariant}
                    className="vm-chip inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-white/90 text-sm"
                    whileHover={reduceMotion ? undefined : { scale: 1.03 }}
                  >
                    {i === 0 ? (
                      <motion.span animate={reduceMotion ? undefined : { rotate: [0, -10, 0] }} transition={{ repeat: Infinity, repeatDelay: 3.2, duration: 1.3 }}>
                        <Shield className="w-4 h-4 text-sky-300" />
                      </motion.span>
                    ) : i === 1 ? (
                      <motion.span animate={reduceMotion ? undefined : { y: [0, -2, 0] }} transition={{ repeat: Infinity, repeatDelay: 2.8, duration: 1.1 }}>
                        <Building className="w-4 h-4 text-sky-300" />
                      </motion.span>
                    ) : i === 2 ? (
                      <motion.span animate={reduceMotion ? undefined : { scale: [1, 1.08, 1] }} transition={{ repeat: Infinity, repeatDelay: 2.6, duration: 1.0 }}>
                        <Handshake className="w-4 h-4 text-sky-300" />
                      </motion.span>
                    ) : (
                      <motion.span animate={reduceMotion ? undefined : { scale: [1, 1.12, 1] }} transition={{ repeat: Infinity, repeatDelay: 2.4, duration: 1.0 }}>
                        <Zap className="w-4 h-4 text-sky-300" />
                      </motion.span>
                    )}
                    {label}
                  </motion.span>
                ))}
              </motion.div>
            </motion.div>
          </div>

          {/* CTA */}
          <motion.div {...fadeUp(0.25)} className={`mt-8 md:mt-10 ${isRTL ? 'text-right' : 'text-left'}`}>
            <button
              onClick={() => { window.location.href = '/about'; }}
              className="inline-flex items-center gap-2 rounded-lg px-6 py-3 text-white"
              style={{ border: '1px solid rgba(255,255,255,.24)', background: 'rgba(255,255,255,.06)', backdropFilter: 'blur(6px)' }}
              aria-label={content.cta}
              onMouseEnter={(e) => {
                if (reduceMotion) return;
                (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 10px 24px rgba(0,0,0,0.25)';
                (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                if (reduceMotion) return;
                (e.currentTarget as HTMLButtonElement).style.boxShadow = '';
                (e.currentTarget as HTMLButtonElement).style.transform = '';
              }}
            >
              {content.cta}
              <svg className={`w-4 h-4 transition-transform ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </motion.div>
        </div>
      </div>
    </SectionFrame>
  );
};

export default VisionMission;
